// Post-event feedback — the question model, timing, and per-job outcome copy.
// Pure JS (no React/Supabase) so the capture form, the brief page, the supplier
// Briefs column and any test all agree. Aggregation across jobs lives in
// supplierPerformance.js; this file is about ONE job's feedback.

// The four questions, in order. Each option carries a tone (good/warn/bad) for
// styling and a `note` flag — a bad answer PROMPTS for a note (never requires
// it). 'reportable' additionally escalates (safety incident).
export const FEEDBACK_QUESTIONS = [
  {
    key: 'delivered',
    label: 'Did they deliver what was agreed?',
    options: [
      { value: 'yes',    label: 'Yes',    tone: 'good' },
      { value: 'mostly', label: 'Mostly', tone: 'warn' },
      { value: 'no',     label: 'No',     tone: 'bad', note: true },
    ],
  },
  {
    key: 'on_time',
    label: 'Were they on time?',
    options: [
      { value: 'yes',     label: 'Yes',          tone: 'good' },
      { value: 'late',    label: 'Late',         tone: 'warn' },
      { value: 'no_show', label: "Didn't show",  tone: 'bad', note: true },
    ],
  },
  {
    key: 'safety',
    label: 'Any safety concerns?',
    options: [
      { value: 'none',       label: 'None',       tone: 'good' },
      { value: 'minor',      label: 'Minor',      tone: 'warn' },
      { value: 'reportable', label: 'Reportable', tone: 'bad', note: true, escalate: true },
    ],
  },
  {
    key: 'use_again',
    label: 'Would you use them again?',
    options: [
      { value: 'yes',          label: 'Yes',               tone: 'good' },
      { value: 'reservations', label: 'With reservations', tone: 'warn' },
      { value: 'no',           label: 'No',                tone: 'bad', note: true },
    ],
  },
];

export const FEEDBACK_KEYS = FEEDBACK_QUESTIONS.map(q => q.key);

// Human label for a stored value (e.g. on_time='no_show' → "Didn't show").
export const feedbackAnswerLabel = (key, value) => {
  const q = FEEDBACK_QUESTIONS.find(x => x.key === key);
  return q?.options.find(o => o.value === value)?.label || value || '—';
};
export const feedbackAnswerTone = (key, value) => {
  const q = FEEDBACK_QUESTIONS.find(x => x.key === key);
  return q?.options.find(o => o.value === value)?.tone || null;
};

// The values that prompt for a note / escalate.
export const answerNeedsNote = (key, value) => {
  const q = FEEDBACK_QUESTIONS.find(x => x.key === key);
  return !!q?.options.find(o => o.value === value)?.note;
};
export const isReportable = (safety) => safety === 'reportable';

// ---------------------------------------------------------------------------
// Timing. Fires seven days after the event end date; with no event date, award
// date + 30 days. The award date is the brief's close (award happens at close).
// ---------------------------------------------------------------------------
const MS_DAY = 24 * 60 * 60 * 1000;

export function feedbackDueDate(brief) {
  if (!brief) return null;
  const eventEnd = brief.event_live_end_date || brief.event_live_date || null;
  if (eventEnd) return new Date(new Date(eventEnd).getTime() + 7 * MS_DAY);
  const awardDate = brief.closed_at || null;
  if (awardDate) return new Date(new Date(awardDate).getTime() + 30 * MS_DAY);
  return null;
}

// One reminder a further 7 days on; after that it lapses.
export function feedbackLapseDate(brief) {
  const due = feedbackDueDate(brief);
  return due ? new Date(due.getTime() + 7 * MS_DAY) : null;
}

// The event's end date for display (what "finished X days ago" counts from).
// event end, else event date, else the award (close) date.
export function feedbackEventDate(brief) {
  return brief?.event_live_end_date || brief?.event_live_date || brief?.closed_at || null;
}

// Whole days since the event finished (0 = today). null when there's no date.
export function feedbackDaysSinceEvent(brief, now = new Date()) {
  const end = feedbackEventDate(brief);
  if (!end) return null;
  return Math.max(0, Math.floor((now.getTime() - new Date(end).getTime()) / MS_DAY));
}

// "The event finished 12 days ago" — decides whether it's still worth recording
// (a month on, nobody remembers). Falls back cleanly with no date.
export function eventFinishedPhrase(brief, now = new Date()) {
  const d = feedbackDaysSinceEvent(brief, now);
  if (d == null) return 'The event has finished';
  if (d === 0) return 'The event finished today';
  if (d === 1) return 'The event finished yesterday';
  return `The event finished ${d} days ago`;
}

// Is this an awarded brief that qualifies for a feedback request at all?
// Below the value threshold there's no request and no lapsed record.
export function feedbackApplies(brief, { thresholdGBP = 0 } = {}) {
  if (!brief) return false;
  if (brief.status !== 'closed' || !brief.awarded_supplier_id) return false;
  const amount = Number(brief.awarded_amount);
  if (Number.isFinite(amount) && amount < (Number(thresholdGBP) || 0)) return false;
  return true;
}

// The feedback record's display state, given the brief + its feedback row (or
// null). Derived so the brief page + Briefs column + reporting agree.
//   n/a       — not an awarded brief, or below threshold
//   not_due   — awarded but before event + 7 days
//   due       — prompt showing (owner should give feedback)
//   complete  — answered
//   lapsed    — never given, past the reminder window
export function feedbackState(brief, feedback, { thresholdGBP = 0, now = new Date() } = {}) {
  if (!feedbackApplies(brief, { thresholdGBP })) return 'n/a';
  if (feedback?.status === 'cancelled') return 'n/a';
  if (feedback?.completed_at || feedback?.status === 'complete') return 'complete';
  const due = feedbackDueDate(brief);
  if (!due || now.getTime() < due.getTime()) return 'not_due';
  const lapse = feedbackLapseDate(brief);
  if (feedback?.status === 'lapsed' || (lapse && now.getTime() >= lapse.getTime())) return 'lapsed';
  return 'due';
}

// The "feedback due" work-list — awarded briefs whose event has passed and
// feedback isn't recorded (state 'due' or 'lapsed'; lapsed still appears,
// marked, until recorded or dismissed — §5). Sorted OLDEST EVENT FIRST because
// feedback decays. Shared by the Briefs "Feedback due" queue + the dashboard
// alert so every count derives from one place (§9). `feedbackByBriefId` maps a
// brief id → its brief_feedback row (or absent). Single award per brief today,
// so one row per brief; when split-award lands this becomes one row per award.
export function deriveFeedbackDue(briefs, feedbackByBriefId = {}, { thresholdGBP = 0, now = new Date() } = {}) {
  const out = [];
  for (const brief of (briefs || [])) {
    const fb = feedbackByBriefId[brief.id] || null;
    const state = feedbackState(brief, fb, { thresholdGBP, now });
    if (state !== 'due' && state !== 'lapsed') continue;
    const amount = Number(brief.awarded_amount);
    out.push({
      brief, feedback: fb, state,
      dueDate: feedbackDueDate(brief),
      eventDate: feedbackEventDate(brief),
      daysSinceEvent: feedbackDaysSinceEvent(brief, now),
      awardValue: Number.isFinite(amount) ? amount : null,
      supplierId: brief.awarded_supplier_id || null,
    });
  }
  out.sort((a, b) => {
    const ax = a.eventDate ? new Date(a.eventDate).getTime() : Infinity;
    const bx = b.eventDate ? new Date(b.eventDate).getTime() : Infinity;
    return ax - bx; // oldest event first
  });
  return out;
}

// Amendable for 30 days after completion.
export function feedbackAmendable(feedback, now = new Date()) {
  if (!feedback?.completed_at) return false;
  return now.getTime() - new Date(feedback.completed_at).getTime() <= 30 * MS_DAY;
}

// Short "how it went" label for the supplier Briefs column / compact record.
export function feedbackOutcomeLabel(state, feedback) {
  if (state === 'due') return { text: 'Feedback due', tone: 'warn' };
  if (state === 'lapsed') return { text: 'Not given', tone: 'muted' };
  if (state !== 'complete' || !feedback) return null;
  if (feedback.safety === 'reportable') return { text: 'Reportable incident', tone: 'bad' };
  const negative = feedback.delivered === 'no' || feedback.on_time === 'no_show' || feedback.use_again === 'no';
  if (negative) return { text: 'Issues raised', tone: 'bad' };
  const minor = feedback.delivered === 'mostly' || feedback.on_time === 'late' || feedback.safety === 'minor' || feedback.use_again === 'reservations';
  return minor ? { text: 'Mostly went well', tone: 'warn' } : { text: 'Went well', tone: 'good' };
}
